<template>
    <div>
        <page-header></page-header>

        <div class="header-section">
            <h1>IMPORT TRAINEES</h1>
            <p>{{ fileName }}</p>
            <button v-if="skippedTrainees.length !== importedTrainees.length" class="export-button" @click="exportToCSV">Export to CSV</button>
            <button class="back-button" @click="goBack">Back</button>
        </div>

        <div :class="{ 'error-info-box': skippedTrainees.length === importedTrainees.length }" class="info-box">
            <div v-if="skippedTrainees.length === importedTrainees.length">
                Trainee Import - Unsuccessful.
                <li>Total record(s): {{ importedTrainees ? importedTrainees.length : 0 }}</li>
                <li>Valid Trainee(s): {{ validTraineeCount }}</li>
                <li style="color:red">Skipped Trainee(s): {{ skippedTrainees ? skippedTrainees.length : 0 }}</li>
                <p></p>
                Trainee(s) added to Database: {{ validTraineeCount }}
            </div>
            <div v-else>
                Trainees Import - Successful!
                <li>Total record(s): {{ importedTrainees.length }}</li>
                <li>New Trainee account(s) created: {{ traineePasswords.length }}</li>
                <li>Trainee role added to existing account(s): {{ roleAddedTrainees.length }}</li>
                <li style="color:red">Skipped Trainee(s): {{ skippedTrainees.length }}</li>
                <p></p>
                Trainee(s) added to Database: {{ validTraineeCount }}
            </div>
        </div>

        <!-- New accounts with temporary passwords -->
        <div class="container-fluid-trainee" style="margin-top: 20px;" v-if="traineePasswords.length">
            <h2 style="margin-left: 20px; font-size: 20pt;">New Accounts - Temporary Passwords:</h2>
            <table class="data-table-trainee">
                <thead>
                    <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>UIN</th>
                        <th>Temporary Password</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in traineePasswords" :key="item.user_id">
                        <td>{{ item.first_name }}</td>
                        <td>{{ item.last_name }}</td>
                        <td>{{ item.email }}</td>
                        <td>{{ item.UIN }}</td>
                        <td>{{ item.temporary_password }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Existing users who had trainee role added -->
        <div class="container-fluid-trainee" style="margin-top: 20px;" v-if="roleAddedTrainees.length">
            <h2 style="margin-left: 20px; font-size: 20pt;">Trainee Role Added to Existing Accounts:</h2>
            <p style="margin-left: 20px; color: #555;">
                These users already had an account. The trainee role has been added — they should continue using their existing password.
            </p>
            <table class="data-table-trainee">
                <thead>
                    <tr>
                        <th>First Name</th>
                        <th>Last Name</th>
                        <th>Email</th>
                        <th>UIN</th>
                        <th>Note</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="item in roleAddedTrainees" :key="item.user_id">
                        <td>{{ item.first_name }}</td>
                        <td>{{ item.last_name }}</td>
                        <td>{{ item.email }}</td>
                        <td>{{ item.license_no }}</td>
                        <td style="color: #2a7ae2; font-style: italic;">
                            Trainee role added - use existing password
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Skipped trainees -->
        <div class="skippedTraineesDisplay">
            <div v-if="skippedTrainees.length">
                <div v-for="item in skippedTrainees" :key="item.email ? item.email : Math.random()">
                    <ul v-if="item">
                        <li style="font-weight:bold; white-space: pre-line;">
                            Skipped Trainee - {{ item.email }}: {{ item.message }}
                        </li>
                    </ul>
                    <i v-if="item" style="color:grey; margin-left:20px;">Provided fields</i>
                    <div v-if="item" class="trainee-list">
                        <ol>
                            <li><strong>First Name: &nbsp;</strong>{{ item.first_name }}</li>
                            <li><strong>Last Name: &nbsp;</strong>{{ item.last_name }}</li>
                            <li><strong>Email: &nbsp;</strong>{{ item.email }}</li>
                            <li v-if="ENABLE_USER_BIRTH_AND_CATEGORY"><strong>Date of Birth: &nbsp;</strong>{{ item.date_of_birth }}</li>
                            <li v-if="ENABLE_USER_BIRTH_AND_CATEGORY"><strong>Place of Birth: &nbsp;</strong>{{ item.place_of_birth }}</li>
                            <li><strong>Professional ID: &nbsp;</strong>{{ item.license_no }}</li>
                            <li><strong>{{ $labels.project }}(s): &nbsp;</strong>{{ item.projects }}</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import pageHeader from '@/components/Examiner/Header.vue';
import { ENABLE_USER_BIRTH_AND_CATEGORY } from '@/config/featureFlags';

export default {
    name: 'TraineeImportSuccessful',
    data() {
        return {
            traineePasswords: [],
        };
    },
    components: {
        pageHeader,
    },
    computed: {
        ENABLE_USER_BIRTH_AND_CATEGORY() {
            return ENABLE_USER_BIRTH_AND_CATEGORY;
        },
        skippedTrainees() { return this.$store.state.skippedTrainees; },
        importedTrainees() { return this.$store.state.importedTrainees; },
        roleAddedTrainees() { return this.$store.state.roleAddedTrainees; },
        validTraineeCount() { return this.importedTrainees.length - this.skippedTrainees.length; },
        fileName() { return this.$store.state.fileName; },
    },
    mounted() {
        this.traineePasswords = this.$store.state.traineePasswords;
    },
    methods: {
        goBack() {
            this.$router.push({ name: 'ImportTrainees' });
        },
        exportToCSV() {
            const headers = ['First Name', 'Last Name', 'Email', 'UIN', 'Temporary Password'];
            const rows = this.traineePasswords.map(t => [t.first_name, t.last_name, t.email, t.UIN, t.temporary_password]);
            let csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].map(e => e.join(",")).join("\n");
            const link = document.createElement("a");
            const now = new Date();
            const dateTime = `${String(now.getDate()).padStart(2,'0')}_${String(now.getMonth()+1).padStart(2,'0')}_${now.getFullYear()}_${String(now.getHours()).padStart(2,'0')}_${String(now.getMinutes()).padStart(2,'0')}_${String(now.getSeconds()).padStart(2,'0')}`;
            link.setAttribute("href", encodeURI(csvContent));
            link.setAttribute("download", `trainee_passwords_${dateTime}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
    },
}
</script>

<style scoped>
.container-fluid-trainee {
  padding-top: 5px;
  margin-left: 1%;
  margin-right: 1%;
}

.header-section {
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center; 
}

.header-section p {
    margin-right: 20%;
    margin-top: 15px;
    color: grey;
    font-size: 25px;
}

.header-section h1 {
    min-width: 300px;
    font-size: 25px;
}

.info-box {
    border: 1px solid #ccc;
    padding: 20px;
    margin: 0px 20px;
    color: #137e2a;
    font-weight: bold;
    background-color: #bfeec7;
    width: 60%;
    height: 200px;
    text-align: left;
}

.error-info-box {
    border: 1px solid var(--error-border);
    border-left: 4px solid var(--error-accent);
    border-radius: 8px;
    padding: 20px;
    margin: 0px 20px;
    color: var(--error-text);
    font-weight: bold;
    background-color: var(--error-bg);
    width: 60%;
    height: 200px;
    text-align: left;
}


.skippedTraineesDisplay {
    text-align: left;
    margin-top: 10px;
    margin-left: 1%;
    background-color: rgb(250, 248, 248);
    width: 98%;
}

.trainee-list {
    background-color: #f0e7e7;
}

.back-button {
  width: 100px;
  border-radius: 5px;
  background-color: #c1e0bf;
  font-size: 14px;
  border: 2px solid black;
  padding: 3px;
  height: 30px;
  margin-right: 20px;
}

.back-button:hover {
  background-color: #abc7a9;
  cursor: pointer;
}
.data-table-trainee, .data-table-trainee th, .data-table-trainee td {
  border: 1px solid black;
}

.data-table-trainee th, .data-table-trainee td {
  padding: 8px;
}

.data-table-trainee td.center-icons {
  text-align: center;
}

/* Data table styles */
.data-table-trainee {
  width: 98%;
  margin-left: 0.5%;
  margin-right: 0.5%;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  border-collapse: collapse;
  margin-top: 0;
  align-items: left;
  margin-bottom: 30px;
}

.data-table-trainee th {
  border: 1px solid #000000bf;
  padding: 10px 5px;
  text-align: left;
  color: rgb(107, 107, 107);
  background-color: rgba(219, 219, 219, 0.804);
  margin-top: 0;
  font-family: 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
}

.data-table-trainee tbody tr:nth-child(odd) {
  background-color: white;
}

.data-table-trainee tbody tr:nth-child(even) {
  background-color: rgba(219, 219, 219, 0.804);
}


.data-table-trainee td {
  border: 1px solid #000000bf;
  padding: 10px 5px;
  text-align: left;
  font-weight: 500;
  margin-top: 0;
  font-family: 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 14px;
}

.export-button {
  width: 200px;
  padding: 5px 5px;
  background-color: #48a0ff;
  color: white;
  border: 1px solid #007bff;
  border-radius: 8px;
  cursor: pointer;
  height: 40px;
  float: right;
}
.export-button:hover {
  background-color: #0056b3;
}
</style>